import styled from '@emotion/styled';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { useMutation } from 'react-query';
import { Text } from '@hotjar/hotsauce';
import type { GlobalModalProps } from '@hotjar/webapp/features/GlobalModal/createGlobalModal';
import { createGlobalModal } from '@hotjar/webapp/features/GlobalModal/createGlobalModal';
import { GenericMessageModal } from '@hotjar/webapp/components/GenericModal/GenericMessageModal';
import { MODAL_SIZES } from '@hotjar/webapp/components/GenericModal';
import { paymentMethodAPI } from '@hotjar/webapp/domains/billing/paymentMethod';
import { useOwnedAccount } from '@hotjar/account';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useEffectOnceWithDependencies } from '@hotjar/webapp/hooks/useEffectOnceWithDependencies';

const S = {
  Text: styled(Text)`
    margin: 0;
  `,
};

const MODAL_NAME = 'Delete Payment Method Confirmation';

export function DeleteBasicPaymentMethod({
  onSubmit,
  onCancel,
}: GlobalModalProps<void>): JSX.Element {
  const { t } = useTranslationWithPrefix('billing:payment-details.delete');
  const { t: tCommon } = useTranslationWithPrefix('common:actions');
  const { data: account } = useOwnedAccount();
  const { loadedModal, closedModal, getGroupDataForAccount, click } = useTrack();

  const deletePaymentMethod = useMutation(() => paymentMethodAPI.remove(account?.id as number), {
    onSuccess: () => onSubmit(),
    onError: () => onCancel(new Error('failed to delete payment method')),
  });

  useEffectOnceWithDependencies(
    () => {
      loadedModal(MODAL_NAME, account ? getGroupDataForAccount(account) : undefined);
    },
    [loadedModal, account, getGroupDataForAccount],
    () => account !== undefined,
  );

  const handleModalClose = () => {
    closedModal(MODAL_NAME, account ? getGroupDataForAccount(account) : undefined);
    onCancel();
  };

  return (
    <GenericMessageModal
      size={MODAL_SIZES.xSmall}
      title={t('basic-plan.title')}
      isLoading={deletePaymentMethod.isLoading}
      description={t<string, string[]>('basic-plan.description', { returnObjects: true }).map(
        (text) => (
          <S.Text as="p" key={text}>
            {text}
          </S.Text>
        ),
      )}
      isOpen
      cancel={{ text: tCommon('cancel'), onClick: () => handleModalClose() }}
      submit={{
        text: t('basic-plan.delete'),
        kind: 'danger',
        onClick: () => {
          click(
            'Confirm Delete Payment Method',
            MODAL_NAME,
            account ? getGroupDataForAccount(account) : {},
          );
          deletePaymentMethod.mutate();
        },
      }}
    />
  );
}

export const openDeleteBasicPaymentMethodModal = createGlobalModal<void, void, Error>(
  DeleteBasicPaymentMethod,
);
